import React, { useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Checkbox, Input } from 'ncoded-component-library';
import {
  getArrayLength,
  getHasElementsInArray,
  getUserFullNameOrEmail,
} from 'utils';
import SearchIcon from 'icons/Search.icon';
import MembersAccessGroupItem from '../MembersAccessGroupItem';
import { DataRoomMember } from 'router/subrouters/DataRoom/models/DataRoomMember';
import { DataRoomMembersAccessGroup } from 'router/subrouters/DataRoom/models/DataRoomRfi';
import { filterMembers, getGroupSelectionState } from './utils';

import './MembersGroupsPicker.styles.scss';

type MembersGroupsPickerProps = {
  className?: string;
  individualBuyers: DataRoomMember[];
  multiMemberGroups: DataRoomMembersAccessGroup[];
  sellSideGroup: DataRoomMembersAccessGroup;
  selectedIds: string[];
  onToggleMember: (memberId: string) => void;
  onToggleGroup: (members: DataRoomMember[]) => void;
};

const MembersGroupsPicker: React.FC<MembersGroupsPickerProps> = (props) => {
  const {
    className,
    individualBuyers,
    multiMemberGroups,
    sellSideGroup,
    selectedIds,
    onToggleMember,
    onToggleGroup,
  } = props;

  const { t } = useTranslation();

  const [search, setSearch] = useState('');

  const classes = classNames('members-groups-picker', className);

  const filteredIndividualBuyers = useMemo(
    () => filterMembers(individualBuyers, search),
    [individualBuyers, search],
  );

  const filteredMultiMemberGroups = useMemo(
    () =>
      multiMemberGroups
        .map((group) => ({
          ...group,
          members: filterMembers(group.members, search),
        }))
        .filter(({ members }) => getHasElementsInArray(members)),
    [multiMemberGroups, search],
  );

  const filteredSellSideGroup = useMemo(() => {
    if (!sellSideGroup) return null;

    const members = filterMembers(sellSideGroup.members, search);

    return getHasElementsInArray(members)
      ? { ...sellSideGroup, members }
      : null;
  }, [sellSideGroup, search]);

  const individualBuyersSelectionState = useMemo(
    () => getGroupSelectionState(filteredIndividualBuyers, selectedIds),
    [filteredIndividualBuyers, selectedIds],
  );

  const buyerGroupsMembers = useMemo(
    () => filteredMultiMemberGroups.flatMap(({ members }) => members),
    [filteredMultiMemberGroups],
  );

  const buyerGroupsSelectionState = useMemo(
    () => getGroupSelectionState(buyerGroupsMembers, selectedIds),
    [buyerGroupsMembers, selectedIds],
  );

  const sellSideSelectionState = useMemo(
    () => getGroupSelectionState(filteredSellSideGroup?.members, selectedIds),
    [filteredSellSideGroup, selectedIds],
  );

  const hasAnyMembers =
    getHasElementsInArray(individualBuyers) ||
    getHasElementsInArray(multiMemberGroups) ||
    getHasElementsInArray(sellSideGroup?.members);

  const hasResults =
    getHasElementsInArray(filteredIndividualBuyers) ||
    getHasElementsInArray(filteredMultiMemberGroups) ||
    !!filteredSellSideGroup;

  const renderFlatRow = useCallback(
    (member: DataRoomMember) => (
      <Checkbox
        key={member._id}
        className={classNames('members-groups-picker__flat-row', {
          selected: selectedIds.includes(member._id),
        })}
        label={getUserFullNameOrEmail(member)}
        checked={selectedIds.includes(member._id)}
        onChange={() => onToggleMember(member._id)}
      />
    ),
    [selectedIds, onToggleMember],
  );

  return (
    <div className={classes}>
      <p className="members-groups-picker__title">{t('chooseMembers')}</p>

      <Input
        placeholder={`${t('searchMembers')}...`}
        prefixNode={<SearchIcon />}
        disabled={!hasAnyMembers}
        value={search}
        onChange={({ target: { value } }) => setSearch(value)}
      />

      <div className="members-groups-picker__groups">
        {hasResults ? (
          <>
            {getHasElementsInArray(filteredIndividualBuyers) && (
              <>
                <div className="members-groups-picker__section-header">
                  <p className="members-groups-picker__section-title">
                    {t('individualBuyers', {
                      count: getArrayLength(filteredIndividualBuyers),
                    })}
                  </p>

                  <Checkbox
                    checked={individualBuyersSelectionState.allSelected}
                    className={classNames({
                      mixed: individualBuyersSelectionState.mixed,
                    })}
                    onChange={() => onToggleGroup(filteredIndividualBuyers)}
                  />
                </div>

                {filteredIndividualBuyers.map(renderFlatRow)}
              </>
            )}

            {getHasElementsInArray(filteredMultiMemberGroups) && (
              <>
                <div className="members-groups-picker__section-header">
                  <p className="members-groups-picker__section-title">
                    {t('buyerGroups', {
                      count: getArrayLength(filteredMultiMemberGroups),
                    })}
                  </p>

                  <Checkbox
                    checked={buyerGroupsSelectionState.allSelected}
                    className={classNames({
                      mixed: buyerGroupsSelectionState.mixed,
                    })}
                    onChange={() => onToggleGroup(buyerGroupsMembers)}
                  />
                </div>

                {filteredMultiMemberGroups.map((group) => (
                  <MembersAccessGroupItem
                    key={group.id}
                    group={group}
                    selectedIds={selectedIds}
                    onToggleMember={onToggleMember}
                    onToggleGroup={onToggleGroup}
                  />
                ))}
              </>
            )}

            {filteredSellSideGroup && (
              <>
                <div className="members-groups-picker__section-header">
                  <p className="members-groups-picker__section-title">
                    {`${t('sellSide')} (${getArrayLength(
                      filteredSellSideGroup.members,
                    )})`}
                  </p>

                  <Checkbox
                    checked={sellSideSelectionState.allSelected}
                    className={classNames({
                      mixed: sellSideSelectionState.mixed,
                    })}
                    onChange={() =>
                      onToggleGroup(filteredSellSideGroup.members)
                    }
                  />
                </div>

                {filteredSellSideGroup.members.map(renderFlatRow)}
              </>
            )}
          </>
        ) : (
          <p className="members-groups-picker__empty">
            {search
              ? t('noMembersMatchSearch')
              : t('noAvailableMembersToGrantAccess')}
          </p>
        )}
      </div>
    </div>
  );
};

export default MembersGroupsPicker;
